/**
 * useTenantSupportEmail
 *
 * Lazily fetches and module-level caches the tenant contact details from the
 * tenant-settings API so the sidebar can display them without repeated requests.
 *
 * Cache is keyed on hospital_id (NOT hostname) so it works correctly in
 * multi-tenant setups — this must match the same identifier used to save
 * these fields (Settings → Hospital Configuration), otherwise a hostname
 * shared by multiple tenants (e.g. shared dev/QA domains) could resolve to
 * a different tenant's stale values.
 *
 * Because it reads by `hospital_id`, this is also the authoritative source for
 * the tenant's MRN term, and it republishes it via `setMrnLabel` (SV-334). The
 * login page publishes a provisional term from the pre-auth tenant-config
 * response, which is resolved by a non-unique hostname scan and can name a
 * different tenant than the one the user authenticates into; this fetch is what
 * corrects it. Called on every login from `authStore._runPostLoginSideEffects`.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';
import { setMrnLabel } from '@/shared/composables/useMrnLabel';

export type TenantContactDetails = {
  email: string | null;
  contactName: string | null;
  facilityName: string | null;
  contactPhone: string | null;
  /**
   * The tenant's term for the medical record number — a display LABEL, not a
   * value prefix, despite the `mrn_prefix` attribute name it comes from (SV-334).
   *
   * The label reaches the UI as the `{{mrnLabel}}` i18next interpolation
   * variable rather than being read from here directly; this composable feeds it
   * to `setMrnLabel` because it is the only reader scoped to the authenticated
   * `hospital_id`. See the module docblock.
   */
  mrnPrefix: string | null;
};

type TenantSupportEmailFields = {
  support_email_id?: string | null;
  support_contact_name?: string | null;
  support_facility_name?: string | null;
  support_contact_phone?: string | null;
  /** Holds the MRN display label, not a value prefix — see `TenantContactDetails.mrnPrefix`. */
  mrn_prefix?: string | null;
};

/** Module-level cache: hospital_id → TenantContactDetails */
const _cache = new Map<string, TenantContactDetails>();
let _fetchPromise: Promise<void> | null = null;

const _supportEmail = ref<string | null>(null);
const _contactName = ref<string | null>(null);
const _facilityName = ref<string | null>(null);
const _contactPhone = ref<string | null>(null);
const _mrnPrefix = ref<string | null>(null);
const _isLoading = ref(false);

/** Bust the cache for the current hospital — call after contact details are saved. */
export function clearSupportEmailCache(): void {
  const hospitalId = config.hospitalId;
  if (hospitalId) _cache.delete(hospitalId);
  _fetchPromise = null;
  _supportEmail.value = null;
  _contactName.value = null;
  _facilityName.value = null;
  _contactPhone.value = null;
  _mrnPrefix.value = null;
}

export function useTenantSupportEmail() {
  async function ensureSupportEmail(): Promise<void> {
    const hospitalId = config.hospitalId;
    if (!hospitalId) return;

    if (_cache.has(hospitalId)) {
      const cached = _cache.get(hospitalId)!;
      _supportEmail.value = cached.email;
      _contactName.value = cached.contactName;
      _facilityName.value = cached.facilityName;
      _contactPhone.value = cached.contactPhone;
      _mrnPrefix.value = cached.mrnPrefix;
      // Deliberately no setMrnLabel() here. A hit means the fetch below already
      // published this hospital's term earlier in the page session, and the
      // catch branch caches an all-null entry on failure — republishing from the
      // cache would let that failure overwrite a good label with the default.
      return;
    }

    if (_fetchPromise) {
      await _fetchPromise;
      return;
    }

    _isLoading.value = true;
    _fetchPromise = (async () => {
      try {
        const api = getApiClient();
        const response = await api.get<TenantSupportEmailFields>(
          `${API_ROUTES.SETTINGS}/tenant-settings/support-email`,
          { params: { hospital_id: hospitalId } },
        );
        const data = response.data;
        const details: TenantContactDetails = {
          email: data.support_email_id ?? null,
          contactName: data.support_contact_name ?? null,
          facilityName: data.support_facility_name ?? null,
          contactPhone: data.support_contact_phone ?? null,
          mrnPrefix: data.mrn_prefix ?? null,
        };
        _cache.set(hospitalId, details);
        _supportEmail.value = details.email;
        _contactName.value = details.contactName;
        _facilityName.value = details.facilityName;
        _contactPhone.value = details.contactPhone;
        _mrnPrefix.value = details.mrnPrefix;
        // Authoritative publish: this term is scoped to the authenticated
        // hospital, so it supersedes the provisional one the login page took
        // from the hostname-resolved tenant-config. A null/blank value is
        // published too — that is a tenant who configured no term, and
        // setMrnLabel resolves it to the per-language default.
        setMrnLabel(details.mrnPrefix);
      } catch {
        // Non-fatal — fall back to generic message (null values).
        //
        // The MRN term is deliberately NOT published here: a transient failure
        // must not replace a correct label with the default. The provisional
        // term from login stays in force instead.
        const empty: TenantContactDetails = { email: null, contactName: null, facilityName: null, contactPhone: null, mrnPrefix: null };
        _cache.set(hospitalId, empty);
        _supportEmail.value = null;
        _contactName.value = null;
        _facilityName.value = null;
        _contactPhone.value = null;
        _mrnPrefix.value = null;
      } finally {
        _isLoading.value = false;
        _fetchPromise = null;
      }
    })();

    await _fetchPromise;
  }

  return {
    supportEmail: _supportEmail,
    supportContactName: _contactName,
    supportFacilityName: _facilityName,
    supportContactPhone: _contactPhone,
    mrnPrefix: _mrnPrefix,
    isLoading: _isLoading,
    ensureSupportEmail,
  };
}
